import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";

import CalendarPage from "@/features/calendar/CalendarPage.vue";
import PatientProfileDashboard from "@/features/patients/profile-dashboard/PatientProfileDashboardPage.vue";
import PatientsPage from "@/features/patients/persons/PersonsPage.vue";
import LoginPage from "@/views/LoginPage.vue";
import LandingPage from "@/features/landing/LandingPage.vue";
import UserProfile from "@/views/UserProfile.vue";
import MyClinicsPage from "@/features/clinics/MyClinicsPage.vue";
import MyAnamnesesPage from "@/features/anamneses/MyAnamnesesPage.vue";
import GoogleAuthSuccess from "@/views/GoogleAuthSuccess.vue";
import ChooseRolePage from "@/views/ChooseRolePage.vue";
import ResetPasswordPage from "@/views/ResetPasswordPage.vue";
import CnesSearchPage from "@/views/CnesSearchPage.vue";


Vue.use(VueRouter);

// Silencia o erro NavigationDuplicated do vue-router 3.1+
// quando se faz push/replace pra rota em que já estamos.
// (clicar 2x no mesmo botão de navegação, logout estando em "/", etc.)
const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }
  return originalPush.call(this, location).catch((err) => {
    if (err && err.name === "NavigationDuplicated") return err;
    return Promise.reject(err);
  });
};

const originalReplace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject);
  }
  return originalReplace.call(this, location).catch((err) => {
    if (err && err.name === "NavigationDuplicated") return err;
    return Promise.reject(err);
  });
};

const routes = [
  // Admin access (roleId 1 ou 3)
  {
    path: "/calendar-page",
    name: "CalendarPage",
    component: CalendarPage,
    meta: { needsAdminAccess: true },
  },
  {
    path: "/my-clinics-page",
    name: "MyClinicsPage",
    component: MyClinicsPage,
    meta: { needsAdminAccess: true },
  },

  // Doctor access (roleId 1)
  {
    path: "/patient-profile-dashboard",
    name: "PatientProfileDashboard",
    component: PatientProfileDashboard,
    meta: { needsToBeADoctor: true },
  },
  {
    path: "/patients-page",
    name: "PatientsPage",
    component: PatientsPage,
    meta: { needsAdminAccess: true },
  },
  {
    path: "/my-anamneses-page",
    name: "MyAnamnesesPage",
    component: MyAnamnesesPage,
    meta: { needsToBeADoctor: true },
  },
  // salvia-805 — pesquisa de CRM/médicos para o comercial.
  // `needsToBeADoctor` (roleId 1) e não `needsAdminAccess` (1 ou 3) de
  // propósito: a API recusa assistente com 403, e a meta do router tem que
  // espelhar o gate do backend — senão a tela abre e a busca falha.
  {
    path: "/cnes-search",
    name: "CnesSearchPage",
    component: CnesSearchPage,
    meta: { needsToBeADoctor: true },
  },
  {
    path: "/patient-profile-dashboard/:id",
    name: "PatientProfileDetails",
    component: PatientProfileDashboard,
    meta: { needsToBeADoctor: true },
  },

  // Públicas
  { path: "/login-page", name: "LoginPage", component: LoginPage },
  { path: "/", name: "LandingPage", component: LandingPage },
  {
    path: "/auth/google/success",
    name: "GoogleAuthSucess",
    component: GoogleAuthSuccess,
  },
  {
    name: "ChooseRolePage",
    path: "/auth/choose-role",
    component: ChooseRolePage,
  },
  // Destino do link enviado por e-mail: /#/reset-password?token=...&email=...
  {
    path: "/reset-password",
    name: "ResetPasswordPage",
    component: ResetPasswordPage,
  },

  // Apenas logado
  {
    path: "/user-profile",
    name: "UserProfile",
    component: UserProfile,
    meta: { needsToBeLoggedIn: true },
  },
];

const router = new VueRouter({ routes });

function routeNeedsAuth(to) {
  return to.matched.some(
    (record) =>
      record.meta.needsAdminAccess ||
      record.meta.needsToBeADoctor ||
      record.meta.needsToBeLoggedIn,
  );
}

router.beforeEach(async (to, from, next) => {
  try {
    // Se a rota não exige login, segue
    if (!routeNeedsAuth(to)) {
      // Já logado não deve ver landing nem login: manda pro calendário.
      // Cobre "sair e voltar pro app" (reload em "/") verificando a sessão
      // via ensureAuth quando o store ainda está frio.
      if (to.name === "LandingPage" || to.name === "LoginPage") {
        const isAuth =
          store.getters["auth/isAuthenticated"] ||
          (await store.dispatch("auth/ensureAuth").catch(() => false));
        // Só redireciona quem realmente tem acesso ao calendário (roleId 1 ou 3),
        // senão criaria loop (calendar-page volta pra "/").
        if (isAuth && [1, 3].includes(store.getters["auth/roleId"])) {
          return next("/calendar-page");
        }
      }
      return next();
    }

    // Garante auth/usuario carregado (vai fazer request /api/user SOMENTE 1x quando necessário)
    const ok = await store.dispatch("auth/ensureAuth");
    if (!ok) {
      return next({
        path: "/login-page",
        query: { redirect: to.fullPath },
      });
    }

    const roleId = store.getters["auth/roleId"];

    // needsAdminAccess: roleId 1 ou 3
    if (to.matched.some((record) => record.meta.needsAdminAccess)) {
      if ([1, 3].includes(roleId)) return next();
      return next("/");
    }

    // needsToBeADoctor: roleId 1
    if (to.matched.some((record) => record.meta.needsToBeADoctor)) {
      if (roleId === 1) return next();
      return next("/");
    }

    // needsToBeLoggedIn: apenas estar logado basta (ok já garantiu)
    return next();
  } catch (e) {
    // Se deu erro, joga pro login por segurança
    return next("/login-page");
  }
});

export default router;
